import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KavitaMomService } from '../kavita-mom.service';
import { KavitaMOM } from '../counsellors-mom.model';
import { AddMomKavitaComponent } from './add-mom-kavita/add-mom-kavita.component';
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {LogService} from "../../../../../services/log.service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

/**
 * Component for listing and managing MOM with Kavita sessions
 */
@Component({
  selector: 'app-mom-kavita',
  templateUrl: './mom-kavita.component.html',
  styleUrls: ['./mom-kavita.component.scss']
})
export class MomKavitaComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();

  displayedColumns: string[] = ['date', 'participants', 'remark', 'actions'];
  dataSource = new MatTableDataSource<KavitaMOM>([]);
  isLoading = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('viewDialog') viewDialog!: TemplateRef<any>;
  selectedSession: KavitaMOM | null = null;

  constructor(
    private kavitaMomService: KavitaMomService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private mLogService: LogService,
  ) { }

  ngOnInit(): void {
    this.loadSessions();
  }

  /**
   * Load all Kavita MOM sessions
   */
  loadSessions() {
    this.isLoading = true;
    this.kavitaMomService.getAll().pipe(takeUntil(this._destroyed$)).subscribe(
      (sessions: any[]) => {
        this.dataSource.data = sessions.sort((a: any, b: any) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
        console.log(sessions);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Error loading sessions:', error);
        this.isLoading = false;
      }
    );
  }

  /**
   * Open dialog to add new session
   */
  openAddDialog() {
    const dialogRef = this.dialog.open(AddMomKavitaComponent, {
      width: '95%',
      maxWidth: '1400px',
      maxHeight: '90vh', // Add this line
      data: null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'saved' || result === 'updated') {
        this.loadSessions();
      }
    });
  }

  /**
   * Open dialog to edit existing session
   */
  openEditDialog(session: KavitaMOM) {
    const dialogRef = this.dialog.open(AddMomKavitaComponent, {
      width: '95%',
      maxWidth: '1400px',
      data: session
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'saved' || result === 'updated') {
        this.loadSessions();
      }
    });
  }

  /**
   * Delete a session
   */
  deleteSession(session: KavitaMOM) {
    if (confirm('Are you sure you want to delete this session?')) {
      this.kavitaMomService.deleteRow(session.id!).then(() => {
        this.snackBar.open('Session deleted successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
        const activity = {
          date: new Date().getTime(),
          section: 'MOM with Kavita',
          action: 'Deleted',
          description: `MOM with Kavita details deleted by user`,
          currentIp: localStorage.getItem('currentip')!
        };
        this.mLogService.addLog(activity);
        this.loadSessions();
      }).catch((error: any) => {
        this.snackBar.open('Error deleting session: ' + error.message, 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      });
    }
  }

  /**
   * Apply filter to the table
   */
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: any): string {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  }

  /**
   * Get participant names as comma-separated string
   */
  getParticipantNames(participants: string[]): string {
    if (!participants || participants.length === 0) return '';
    return participants.join(', ');
  }

  /**
   * Get count of session items
   */
  getSessionCount(session: KavitaMOM): number {
    return session.sessions?.length || 0;
  }
  openViewDialog(session: KavitaMOM) {
    this.selectedSession = session;

    this.dialog.open(this.viewDialog, {
      width: '900px',
      disableClose: true
    });
  }

  closeView() {
    this.dialog.closeAll();
  }

  /** Firestore-safe date handler */
  getDate(val: any): Date | null {
    if (!val) return null;

    if (val.seconds) {
      return new Date(val.seconds * 1000);
    }

    if (val.toDate) {
      return val.toDate();
    }

    return new Date(val);
  }

  protected readonly PermissionType = PermissionType;

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
